import { useState, useEffect, useRef, useCallback } from 'react'
import { Component, LocaleCode, BigCommerceProductData } from '../types/component'

interface ProductGridProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
  cartId?: string | null
  wishlistId?: string | null
  onCartUpdate?: (cartId: string) => void
  onWishlistUpdate?: (wishlistId: string) => void
}

export function ProductGrid({
  component,
  locale,
  storeId,
  apiUrl,
  style,
  cartId,
  wishlistId,
  onCartUpdate,
  onWishlistUpdate,
}: ProductGridProps) {
  const config = component.config as any

  const sourceType = (config.sourceType as string) || 'manual'
  const categoryId = config.categoryId as number
  const columns = (config.columns as number) || 3
  const gap = (config.gap as string) || '1rem'
  const paginationType = (config.paginationType as string) || 'none'
  const productsPerPage = (config.productsPerPage as number) || 12
  const showSort = (config.showSort as boolean) || false
  const sortOptions = (config.sortOptions as Array<string>) || ['name-asc', 'price-asc', 'price-desc']
  const defaultSort = (config.defaultSort as string) || 'name-asc'

  const [categoryProducts, setCategoryProducts] = useState<BigCommerceProductData[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [addingToCart, setAddingToCart] = useState<Record<string, boolean>>({})
  const [addedToCart, setAddedToCart] = useState<Record<string, boolean>>({})
  const [sortBy, setSortBy] = useState(defaultSort)
  const observerTarget = useRef<HTMLDivElement>(null)

  const loadCategoryProducts = useCallback(async () => {
    if (!storeId) return

    try {
      setIsLoading(true)
      const response = await fetch(
        `${apiUrl}/api/v1/bigcommerce/products/search`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storeId,
            categoryId,
            page: currentPage,
            limit: productsPerPage,
          }),
        }
      )
      if (!response.ok) throw new Error('Failed to load products')
      const data = await response.json()

      if (paginationType === 'infinite' && currentPage > 1) {
        setCategoryProducts((prev) => [...prev, ...(data.products || [])])
      } else {
        setCategoryProducts(data.products || [])
      }

      setTotalPages(data.pagination?.total_pages || 1)
    } catch (error) {
      console.error('Failed to load category products:', error)
    } finally {
      setIsLoading(false)
    }
  }, [storeId, categoryId, currentPage, productsPerPage, paginationType, apiUrl])

  useEffect(() => {
    if (sourceType === 'category' && storeId) {
      loadCategoryProducts()
    }
  }, [sourceType, storeId, currentPage, sortBy, loadCategoryProducts])

  useEffect(() => {
    if (paginationType !== 'infinite' || !observerTarget.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && currentPage < totalPages && !isLoading) {
          setCurrentPage((prev) => prev + 1)
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(observerTarget.current)
    return () => observer.disconnect()
  }, [paginationType, currentPage, totalPages, isLoading])

  const handleAddToCart = async (product: BigCommerceProductData) => {
    if (!storeId) return

    const lineItem = {
      product_id: Number(product.productId),
      quantity: 1,
    }

    try {
      setAddingToCart((prev) => ({ ...prev, [product.productId]: true }))

      let currentCartId = cartId
      if (!currentCartId) {
        const response = await fetch(`${apiUrl}/api/v1/bigcommerce/carts/${storeId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lineItems: [lineItem] }),
        })
        if (!response.ok) throw new Error('Failed to create cart')
        const data = await response.json()
        currentCartId = data.data.id
      } else {
        const response = await fetch(`${apiUrl}/api/v1/bigcommerce/carts/${storeId}/${currentCartId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lineItems: [lineItem] }),
        })
        if (!response.ok) throw new Error('Failed to add to cart')
      }

      if (onCartUpdate && currentCartId) {
        onCartUpdate(currentCartId)
      }

      setAddedToCart((prev) => ({ ...prev, [product.productId]: true }))
      setTimeout(() => {
        setAddedToCart((prev) => ({ ...prev, [product.productId]: false }))
      }, 2000)
    } catch (error) {
      console.error('Failed to add to cart:', error)
    } finally {
      setAddingToCart((prev) => ({ ...prev, [product.productId]: false }))
    }
  }

  const products = sourceType === 'category' ? categoryProducts : (component.bigcommerceProducts || [])

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.name.localeCompare(b.name)
      case 'name-desc':
        return b.name.localeCompare(a.name)
      case 'price-asc':
        return a.price - b.price
      case 'price-desc':
        return b.price - a.price
      default:
        return 0
    }
  })

  // Apply pagination for manual products (category products are paginated by API)
  const paginatedProducts = sourceType === 'manual' && paginationType !== 'none'
    ? sortedProducts.slice(
        paginationType === 'infinite' ? 0 : (currentPage - 1) * productsPerPage,
        paginationType === 'infinite' ? currentPage * productsPerPage : currentPage * productsPerPage
      )
    : sortedProducts

  // Calculate total pages for manual products
  const manualTotalPages = sourceType === 'manual' ? Math.ceil(sortedProducts.length / productsPerPage) : totalPages

  if (products.length === 0 && !isLoading) {
    return (
      <div style={style} className="border rounded-lg p-4">
        <div className="text-center py-12 text-muted-foreground">
          {sourceType === 'category' ? (
            <>
              <p>No products in this category</p>
              <p className="text-xs mt-1">Select a different category in the properties panel</p>
            </>
          ) : (
            <>
              <p>No products selected</p>
              <p className="text-xs mt-1">Add products in the properties panel</p>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={style}>
      {/* Sort */}
      {showSort && sortOptions.length > 0 && (
        <div className="flex items-center gap-3 mb-6">
          <label className="text-sm font-medium">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border rounded"
          >
            {sortOptions.map((option) => (
              <option key={option} value={option}>
                {option === 'name-asc' && 'Name (A-Z)'}
                {option === 'name-desc' && 'Name (Z-A)'}
                {option === 'price-asc' && 'Price (Low to High)'}
                {option === 'price-desc' && 'Price (High to Low)'}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)`, gap }}>
        {paginatedProducts.map((product) => (
          <div key={product.productId} className="border rounded-lg p-4">
            <div className="aspect-square bg-muted mb-3 rounded overflow-hidden">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                  No Image
                </div>
              )}
            </div>
            <p className="font-semibold text-sm mb-1 line-clamp-2">{product.name}</p>
            <p className="text-lg font-bold mb-2">${product.price.toFixed(2)}</p>
            {config.buttonText && (
              <button
                onClick={() => handleAddToCart(product)}
                disabled={addingToCart[product.productId]}
                className="w-full px-3 py-2 bg-primary text-primary-foreground rounded text-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {addingToCart[product.productId]
                  ? 'Adding...'
                  : addedToCart[product.productId]
                  ? 'Added!'
                  : config.buttonText}
              </button>
            )}
          </div>
        ))}
      </div>

      {isLoading && (
        <div className="text-center py-4 text-muted-foreground">Loading products...</div>
      )}

      {/* Numbered Pagination */}
      {paginationType === 'numbered' && manualTotalPages > 1 && !isLoading && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded hover:bg-accent disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {currentPage} of {manualTotalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(manualTotalPages, currentPage + 1))}
            disabled={currentPage === manualTotalPages}
            className="px-3 py-1 border rounded hover:bg-accent disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Infinite Scroll Target */}
      {paginationType === 'infinite' && currentPage < manualTotalPages && (
        <div ref={observerTarget} className="h-4 mt-4" />
      )}
    </div>
  )
}
